import { useState } from "react";
import { Reveal } from "@components/ui";

interface CookiesSection {
  number: number;
  title: string;
  content: React.ReactNode;
}

function AccordionItem({
  section,
  isOpen,
  onToggle,
}: {
  section: CookiesSection;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={`rounded-2xl border transition-all duration-300 ${
        isOpen
          ? "bg-white border-[#122d63]/15 shadow-[0_2px_16px_rgba(18,45,99,0.06)]"
          : "bg-white border-[#e9e8e6] hover:border-[#d0d0d0] hover:shadow-sm"
      }`}
    >
      <button
        className="w-full flex items-center gap-4 md:gap-5 p-4 md:p-6 text-left cursor-pointer"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <div
          className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center shrink-0 text-[14px] md:text-[16px] font-bold transition-all duration-300 ${
            isOpen
              ? "bg-[#122d63] text-white"
              : "bg-[#f6f6f9] text-[#62636c]"
          }`}
        >
          {String(section.number).padStart(2, "0")}
        </div>
        <span
          className={`flex-1 text-[16px] md:text-[20px] lg:text-[24px] font-semibold tracking-[-0.04em] leading-[1.3] transition-colors duration-200 ${
            isOpen ? "text-[#122d63]" : "text-[#1e1f24]"
          }`}
        >
          {section.title}
        </span>
        <div
          className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
            isOpen ? "bg-[#122d63] rotate-180" : "bg-[#f1f1f1]"
          }`}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            className="transition-colors duration-200"
          >
            <path
              d="M3.5 5.25L7 8.75L10.5 5.25"
              stroke={isOpen ? "white" : "#1e1f24"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>

      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 md:px-6 pb-5 md:pb-6 pt-0">
            <div className="w-full h-px bg-[#f0efed] mb-5" />
            <div className="md:pl-[52px] lg:pl-[68px] text-[14px] md:text-[18px] lg:text-[20px] text-[#62636c] leading-[1.6] tracking-[-0.04em]">
              {section.content}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BulletItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-2 h-2 min-w-2 rounded-full bg-[#122d63]/40 mt-2.5" />
      <span>{children}</span>
    </li>
  );
}

const cookiesSections: CookiesSection[] = [
  {
    number: 1,
    title: "What Are Cookies?",
    content: (
      <p>
        Cookies are small text files that are placed on your device (computer,
        tablet, or smartphone) when you visit a website. They help the website
        recognize your device and remember information about your visit, such as
        your preferences and actions. Cookies are widely used to make websites
        work more efficiently and to provide information to website owners.
      </p>
    ),
  },
  {
    number: 2,
    title: "Why We Use Cookies",
    content: (
      <div className="flex flex-col gap-4">
        <p>We use cookies on www.gazglobal.net to:</p>
        <ul className="flex flex-col gap-3">
          <BulletItem>
            Ensure the website functions correctly and securely.
          </BulletItem>
          <BulletItem>
            Remember your login details and language preferences.
          </BulletItem>
          <BulletItem>
            Understand how visitors interact with our website to improve user
            experience.
          </BulletItem>
          <BulletItem>
            Measure the effectiveness of our marketing campaigns.
          </BulletItem>
          <BulletItem>
            Personalize content and display relevant information based on your
            browsing behavior.
          </BulletItem>
        </ul>
      </div>
    ),
  },
  {
    number: 3,
    title: "Types of Cookies We Use",
    content: (
      <div className="flex flex-col gap-6">
        {[
          {
            heading: "A. Essential Cookies (Strictly Necessary)",
            desc: "These cookies are required for the basic functionality of the website. Without them, certain features like secure login, form submissions, and payment processing will not work.",
            items: [
              "Session management and authentication.",
              "Fraud prevention and security.",
              "Remembering items in your service request or cart.",
            ],
            note: "These cookies cannot be disabled as they are essential for the website to operate.",
          },
          {
            heading: "B. Performance & Analytics Cookies",
            desc: "These cookies collect anonymous data about how visitors use our website — such as which pages are visited most and whether users encounter errors.",
            items: [
              "Google Analytics for traffic reporting.",
              "Heatmaps and session recording tools for UX analysis.",
              "Page load and performance monitoring.",
            ],
          },
          {
            heading: "C. Preference Cookies (Functional)",
            desc: "These cookies allow the website to remember choices you have made, such as language, region, or display preferences, to provide a more personalized experience.",
            items: [
              "Language and region settings.",
              "Display and accessibility preferences.",
              "Previously viewed pages or services.",
            ],
          },
          {
            heading: "D. Marketing & Personalization Cookies",
            desc: "These cookies are used to deliver advertisements that are relevant to you and to measure the effectiveness of our campaigns.",
            items: [
              "Tracking across websites for ad targeting (e.g., Meta Pixel, Google Ads).",
              "Measuring campaign conversions and ROI.",
              "Limiting the number of times you see an ad.",
            ],
            note: "You can opt out of marketing cookies at any time through the Cookie Consent banner or your browser settings.",
          },
        ].map((sub, i) => (
          <div key={i} className="flex flex-col gap-3">
            <p className="font-semibold text-[#1e1f24]">{sub.heading}</p>
            <p>{sub.desc}</p>
            <ul className="flex flex-col gap-2">
              {sub.items.map((item, j) => (
                <BulletItem key={j}>{item}</BulletItem>
              ))}
            </ul>
            {sub.note && (
              <div className="bg-[#f6f6f9] rounded-xl px-5 md:px-8 py-4 border-l-4 border-[#122d63] mt-1">
                <p className="font-medium">{sub.note}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    ),
  },
  {
    number: 4,
    title: "How Long Cookies Are Stored",
    content: (
      <div className="flex flex-col gap-4">
        <p>
          Cookies have different lifespans depending on their type and purpose:
        </p>
        <ul className="flex flex-col gap-3">
          <BulletItem>
            <strong>Session Cookies</strong> — Temporary cookies that are deleted
            when you close your browser. Used for things like keeping you logged
            in during a single visit.
          </BulletItem>
          <BulletItem>
            <strong>Persistent Cookies</strong> — These remain on your device for
            a set period (e.g., 30 days to 2 years) or until you delete them
            manually. Used for remembering preferences and analytics.
          </BulletItem>
        </ul>
      </div>
    ),
  },
  {
    number: 5,
    title: "Managing or Disabling Cookies",
    content: (
      <div className="flex flex-col gap-4">
        <p>
          You have full control over the cookies stored on your device. You can
          manage or disable cookies through:
        </p>
        <ul className="flex flex-col gap-3">
          <BulletItem>
            <strong>Browser Settings</strong> — Most browsers allow you to block
            or delete cookies. Check your browser's help section for
            instructions.
          </BulletItem>
          <BulletItem>
            <strong>Cookie Consent Banner</strong> — When you first visit
            www.gazglobal.net, a banner will allow you to accept or reject
            non-essential cookies.
          </BulletItem>
          <BulletItem>
            <strong>Third-Party Opt-Out Tools</strong> — You can opt out of
            specific analytics or advertising cookies through tools like{" "}
            <strong>Google Analytics Opt-Out</strong> or{" "}
            <strong>Your Online Choices</strong>.
          </BulletItem>
        </ul>
        <div className="bg-[#f6f6f9] rounded-xl px-5 md:px-8 py-4 border-l-4 border-[#122d63]">
          <p className="font-medium">
            Disabling essential cookies may affect the functionality of the
            website, including login and payment features.
          </p>
        </div>
      </div>
    ),
  },
  {
    number: 6,
    title: "Third-Party Cookies",
    content: (
      <div className="flex flex-col gap-4">
        <p>
          Some cookies on our website are placed by third-party services that
          appear on our pages. We do not control these cookies. Examples include:
        </p>
        <ul className="flex flex-col gap-3">
          <BulletItem>
            <strong>Google Analytics</strong> — for website traffic analysis.
          </BulletItem>
          <BulletItem>
            <strong>Google Ads / Meta Pixel</strong> — for marketing performance
            tracking.
          </BulletItem>
          <BulletItem>
            <strong>Payment Gateways</strong> — for secure transaction
            processing.
          </BulletItem>
          <BulletItem>
            <strong>Live Chat Services</strong> — for customer support
            interactions.
          </BulletItem>
        </ul>
        <p>
          We encourage you to review the privacy policies of these third-party
          providers for more information on how they handle your data.
        </p>
      </div>
    ),
  },
  {
    number: 7,
    title: "Data Protection & Privacy",
    content: (
      <div className="flex flex-col gap-4">
        <p>
          Cookies used on www.gazglobal.net comply with the UAE Personal Data
          Protection Law (PDPL) and applicable data protection regulations. We
          ensure:
        </p>
        <ul className="flex flex-col gap-3">
          <BulletItem>
            No personally identifiable information is stored in cookies unless
            absolutely necessary.
          </BulletItem>
          <BulletItem>
            All cookie data is encrypted and stored securely.
          </BulletItem>
          <BulletItem>
            You have the right to withdraw cookie consent at any time.
          </BulletItem>
        </ul>
        <p>
          For more details, please refer to our Privacy Policy (switch to the
          "Privacy Policy" tab above).
        </p>
      </div>
    ),
  },
];

export default function CookiesContentSection() {
  const [openSections, setOpenSections] = useState<Set<number>>(
    new Set([1])
  );

  const toggleSection = (num: number) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(num)) {
        next.delete(num);
      } else {
        next.add(num);
      }
      return next;
    });
  };

  return (
    <section className="w-full bg-[#fefcfc] py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[900px] mx-auto flex flex-col gap-3 md:gap-4">
        {cookiesSections.map((section) => (
          <Reveal key={section.number} direction="up" delay={section.number <= 3 ? section.number * 40 : 0}>
            <AccordionItem
              section={section}
              isOpen={openSections.has(section.number)}
              onToggle={() => toggleSection(section.number)}
            />
          </Reveal>
        ))}
      </div>
    </section>
  );
}
